import { intake } from '@lawbee/domain';
import { Alert, Button, Checkbox, Field, Input, Select, Textarea, cn } from '@lawbee/ui';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import type { Answers, IntakeField, Party } from '../lib/types';

export const PARTY_ROLES: { value: string; label: string }[] = [
  { value: 'opposing_party', label: 'Other side (opposing party)' },
  { value: 'spouse', label: 'Spouse or partner' },
  { value: 'related_party', label: 'Related person or company' },
  { value: 'witness', label: 'Witness' },
  { value: 'other', label: 'Other' },
];

export type IntakeSubmitPayload = { answers: Answers; files: Record<string, File> };

export type IntakeRendererProps = {
  /** Form-specific fields (core contact fields are added unless `includeCore` is false). */
  fields: IntakeField[];
  includeCore?: boolean;
  /** `followup` validates only the listed fields (core identity is already known). */
  mode?: 'intake' | 'followup';
  initialAnswers?: Answers;
  submitLabel?: string;
  /** Preview in the form builder: interactive, but nothing is submitted. */
  preview?: boolean;
  submitting?: boolean;
  /** Field errors returned by the server (400 details). */
  serverErrors?: Record<string, string>;
  onSubmit?: (payload: IntakeSubmitPayload) => void | Promise<void>;
  className?: string;
};

/**
 * Public intake form renderer: core fields + form fields, conditional visibility
 * (`intake.isVisible`), repeatable parties rows, file uploads and client-side validation
 * (`intake.validateIntake`). Missing required details never block a submission: the firm follows up.
 */
export function IntakeRenderer({
  fields,
  includeCore = true,
  mode = 'intake',
  initialAnswers,
  submitLabel = 'Submit',
  preview,
  submitting,
  serverErrors,
  onSubmit,
  className,
}: IntakeRendererProps) {
  const [answers, setAnswers] = useState<Answers>(() => ({ ...(initialAnswers ?? {}) }));
  const [files, setFiles] = useState<Record<string, File>>({});
  const [attempted, setAttempted] = useState(false);
  const [confirmMissing, setConfirmMissing] = useState(false);

  const all = useMemo(() => (includeCore ? [...intake.CORE_FIELDS, ...fields] : fields), [fields, includeCore]);
  const visible = all.filter((f) => intake.isVisible(f, answers, all));

  const validation = useMemo(() => {
    const fileKeys = Object.keys(files);
    if (mode === 'followup') {
      const v = intake.validateIntake(fields, answers, fileKeys);
      const keys = new Set(fields.map((f) => f.key));
      const missing = fields
        .filter((f) => f.required && f.type !== 'checkbox' && intake.isVisible(f, answers, fields))
        .filter((f) => (f.type === 'file' ? !fileKeys.includes(f.key) : intake.isEmpty(answers[f.key])))
        .map((f) => ({ field: f.key, label: f.label }));
      return { errors: v.errors.filter((e) => keys.has(e.field) && !intake.CORE_KEYS.has(e.field)), missing };
    }
    const v = intake.validateIntake(fields, answers, fileKeys);
    return { errors: v.errors, missing: v.missing };
  }, [answers, files, fields, mode]);

  const errorFor = (key: string): string | undefined => {
    if (serverErrors?.[key]) return serverErrors[key];
    if (!attempted) return undefined;
    return validation.errors.find((e) => e.field === key)?.message;
  };

  const set = (key: string, value: Answers[string]) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    setConfirmMissing(false);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setAttempted(true);
    if (preview) return;
    if (validation.errors.length > 0) return;
    if (validation.missing.length > 0 && !confirmMissing) {
      setConfirmMissing(true);
      return;
    }
    // Only visible answers are sent; hidden conditional fields are dropped.
    const out: Answers = {};
    for (const f of visible) {
      if (f.type === 'file') continue;
      const v = answers[f.key];
      if (v !== undefined) out[f.key] = v;
    }
    const outFiles: Record<string, File> = {};
    for (const f of visible) {
      const file = files[f.key];
      if (f.type === 'file' && file) outFiles[f.key] = file;
    }
    await onSubmit?.({ answers: out, files: outFiles });
  };

  return (
    <form noValidate onSubmit={(e) => void handleSubmit(e)} className={cn('space-y-5', className)}>
      {visible.map((f) => (
        <FieldInput
          key={f.key}
          field={f}
          value={answers[f.key]}
          file={files[f.key]}
          error={errorFor(f.key)}
          onChange={(v) => set(f.key, v)}
          onFile={(file) => {
            setFiles((prev) => {
              const next = { ...prev };
              if (file) next[f.key] = file;
              else delete next[f.key];
              return next;
            });
            setConfirmMissing(false);
          }}
        />
      ))}

      {attempted && validation.errors.length > 0 ? (
        <Alert variant="destructive" title="Please check the highlighted fields">
          {validation.errors.length === 1 ? validation.errors[0]!.message : `${validation.errors.length} fields need attention.`}
        </Alert>
      ) : null}

      {attempted && validation.errors.length === 0 && validation.missing.length > 0 ? (
        <Alert variant="warning" title="Some details are missing">
          <span className="block">You can submit now and the firm will ask you for the rest later:</span>
          <ul className="mt-1.5 list-disc pl-5" data-testid="missing-list">
            {validation.missing.map((m) => (
              <li key={m.field}>{m.label}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <div className="flex flex-col-reverse items-stretch gap-3 pt-2 sm:flex-row sm:items-center sm:justify-end">
        {preview ? <p className="text-xs text-muted-foreground sm:mr-auto">Preview only. Nothing is submitted.</p> : null}
        <Button type="submit" size="lg" loading={submitting}>
          {confirmMissing ? 'Submit with missing details' : submitLabel}
        </Button>
      </div>
    </form>
  );
}

function FieldInput({
  field,
  value,
  file,
  error,
  onChange,
  onFile,
}: {
  field: IntakeField;
  value: Answers[string];
  file: File | undefined;
  error: string | undefined;
  onChange: (v: Answers[string]) => void;
  onFile: (file: File | null) => void;
}) {
  const id = `intake-${field.key}`;
  const invalid = error ? true : undefined;
  const common = { id, 'aria-invalid': invalid, 'aria-describedby': field.help ? `${id}-help` : undefined };
  const label = field.label;
  const required = !!field.required;

  switch (field.type) {
    case 'textarea':
      return (
        <Field label={label} htmlFor={id} required={required} description={field.help} error={error}>
          <Textarea {...common} rows={5} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} />
        </Field>
      );
    case 'select':
      return (
        <Field label={label} htmlFor={id} required={required} description={field.help} error={error}>
          <Select
            {...common}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Choose…"
            options={(field.options ?? []).map((o) => ({ value: o, label: o }))}
          />
        </Field>
      );
    case 'multiselect': {
      const list = Array.isArray(value) ? (value as string[]) : [];
      return (
        <fieldset className="space-y-2">
          <legend className="text-[13px] font-medium text-foreground/90">
            {label}
            {required ? <span className="text-destructive"> *</span> : null}
          </legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {(field.options ?? []).map((o) => (
              <Checkbox
                key={o}
                label={o}
                checked={list.includes(o)}
                onCheckedChange={(c) => onChange(c === true ? [...list, o] : list.filter((x) => x !== o))}
              />
            ))}
          </div>
          {field.help ? <p className="text-xs text-muted-foreground">{field.help}</p> : null}
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
        </fieldset>
      );
    }
    case 'checkbox':
      return (
        <div className="space-y-1">
          <Checkbox id={id} label={label} checked={value === true} onCheckedChange={(c) => onChange(c === true)} />
          {field.help ? <p className="pl-[26px] text-xs text-muted-foreground">{field.help}</p> : null}
          {error ? <p className="pl-[26px] text-xs text-destructive">{error}</p> : null}
        </div>
      );
    case 'file':
      return (
        <Field label={label} htmlFor={id} required={required} description={field.help ?? 'PDF, images or documents, up to 10 MB.'} error={error}>
          <input
            {...common}
            type="file"
            className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-secondary file:px-3 file:py-2 file:text-sm file:font-medium file:text-secondary-foreground hover:file:bg-foreground/[0.08]"
            onChange={(e) => onFile(e.target.files?.[0] ?? null)}
          />
          {file ? <p className="text-xs text-muted-foreground">Selected: {file.name}</p> : null}
        </Field>
      );
    case 'parties':
      return <PartiesInput field={field} value={value} error={error} onChange={onChange} />;
    default: {
      const type = field.type === 'phone' ? 'tel' : field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : field.type === 'email' ? 'email' : 'text';
      const autoComplete =
        field.key === 'first_name' ? 'given-name' : field.key === 'last_name' ? 'family-name' : field.key === 'email' ? 'email' : field.key === 'phone' ? 'tel' : undefined;
      return (
        <Field label={label} htmlFor={id} required={required} description={field.help} error={error}>
          <Input
            {...common}
            type={type}
            autoComplete={autoComplete}
            inputMode={field.type === 'number' ? 'decimal' : undefined}
            value={typeof value === 'string' || typeof value === 'number' ? String(value) : ''}
            onChange={(e) => onChange(e.target.value)}
          />
        </Field>
      );
    }
  }
}

function PartiesInput({ field, value, error, onChange }: { field: IntakeField; value: Answers[string]; error?: string; onChange: (v: Party[]) => void }) {
  const defaultRole = field.partyRole ?? 'related_party';
  const rows: Party[] = Array.isArray(value) && value.length > 0 ? (value as Party[]) : [{ name: '', role: defaultRole }];
  const update = (i: number, patch: Partial<Party>) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  return (
    <fieldset className="space-y-2.5">
      <legend className="text-[13px] font-medium text-foreground/90">
        {field.label}
        {field.required ? <span className="text-destructive"> *</span> : null}
      </legend>
      {field.help ? <p className="text-xs text-muted-foreground">{field.help}</p> : null}
      {rows.map((r, i) => (
        <div key={i} className="flex flex-col gap-2 rounded-lg bg-muted/50 p-2.5 sm:flex-row sm:items-center">
          <Input
            aria-label={`Party ${i + 1} name`}
            placeholder="Full name or company"
            value={r.name}
            onChange={(e) => update(i, { name: e.target.value })}
            className="sm:flex-1"
          />
          <Select aria-label={`Party ${i + 1} role`} value={r.role || defaultRole} onChange={(e) => update(i, { role: e.target.value })} options={PARTY_ROLES} className="sm:w-56" />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Remove party ${i + 1}`}
            disabled={rows.length === 1 && !r.name}
            onClick={() => onChange(rows.length === 1 ? [] : rows.filter((_, idx) => idx !== i))}
          >
            <Trash2 />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...rows, { name: '', role: defaultRole }])}>
        <Plus /> Add another person or company
      </Button>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </fieldset>
  );
}

/** Build the multipart body the public intake endpoints expect. */
export function intakeFormData(payload: IntakeSubmitPayload): FormData {
  const fd = new FormData();
  fd.set('data', JSON.stringify({ answers: payload.answers }));
  for (const [key, file] of Object.entries(payload.files)) fd.set(`file:${key}`, file, file.name);
  return fd;
}
